import { Component, OnInit } from '@angular/core';
import { AsyncPipe, DecimalPipe, I18nPluralPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { NgChartsModule } from 'ng2-charts';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SidenavContentService } from '../../shared/sidenav/sidenav-content/sidenav-content.service';
import { NotificationContext, NotificationsListComponent } from '../../notifications/notifications-list/notifications-list.component';
import { NotificationsSidenavComponent } from '../../notifications/notifications-sidenav/notifications-sidenav.component';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { PropertyManagementCompanyService } from 'app/services/property-management-company/property-management-company.service';
import { PropertyManagementCompanyDataService } from 'app/services/property-management-company/property-manager-company-data.service';
import { NotificationsService } from 'app/services/notifications/notifications-service';
import { HostListenerService } from 'app/services/host-listener.service';
import { ScrollService } from 'app/services/scroll.service';
import { PropertyManagementCompanyDateFilters } from 'app/enums/property-management-company-date-filters.enum';
import { DatabaseNotification } from 'app/models/entities/database-notification';
import { GlobalDonutGraphOptions } from 'app/services/communication/graphing.service';
import { ChartCanvasProperties } from 'app/models/entities/forms/organization-form';
import { PropertyManagementCompany } from 'app/models/entities/property-management-company';
import { AnimatedDelayDirective, AnimatedDirective } from '../../shared/directives/animated.directive';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { EmptyStateModule } from '../../shared/empty-state/empty-state.module';
import { MatToolbarModule } from '@angular/material/toolbar';
import { I18nMap } from '../../../../models/entities/i18n-map';

export interface PropertyManagementCompanyChartProperties extends ChartCanvasProperties
{
    total: () => number;
}

@UntilDestroy()
@Component({
    selector: 'app-pmc-dashboard',
    templateUrl: './pmc-dashboard.component.html',
    styleUrls: ['./pmc-dashboard.component.scss'],
    standalone: true,
    imports: [
        MatGridListModule,
        MatCardModule,
        AvatarBlockModule,
        ReactiveFormsModule,
        AsyncPipe,
        DecimalPipe,
        MatFormFieldModule,
        MatSelectModule,
        MatProgressBarModule,
        MatDividerModule,
        NgChartsModule,
        MatListModule,
        MatIconModule,
        NgTemplateOutlet,
        NgIf,
        NgForOf,
        MatButtonModule,
        MatMenuModule,
        ButtonDirective,
        NotificationsListComponent,
        AnimatedDirective,
        AnimatedDelayDirective,
        PageHeaderComponent,
        EmptyStateModule,
        MatToolbarModule,
        I18nPluralPipe,
        MatChipsModule,
    ],
})
export class PmcDashboardComponent implements OnInit
{
    pmc$: Observable<PropertyManagementCompany>;
    filters = this._fb.nonNullable.group({
        requestsFilterByDate: {value: PropertyManagementCompanyDateFilters.CURRENT_MONTH, disabled: true},
        violationsFilterByDate: {value: PropertyManagementCompanyDateFilters.CURRENT_MONTH, disabled: true},
        collectionsFilterByCommunity: {value: -1, disabled: true},
        collectionsFilterByCalendar: {value: 'calendar', disabled: true},
        collectionsFilterByYear: {value: 2024, disabled: true},
    });
    charts: {
        requests: PropertyManagementCompanyChartProperties;
        violations: PropertyManagementCompanyChartProperties;
    };
    chartPlaceholder: PropertyManagementCompanyChartProperties = {
        count: 0,
        dataset: {
            data: [1],
            backgroundColor: ['#ECEEF2'],
            hoverBackgroundColor: ['#ECEEF2'],
            borderColor: ['#FFFFFF'],
            hoverBorderColor: ['#FFFFFF'],
        },
        labels: [''],
        options: {
            ...GlobalDonutGraphOptions,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: false,
            },
        },
        total: () => 0,
    };
    notifications$: Observable<DatabaseNotification[]>;

    readonly organizationsMappingTerm: I18nMap = {
        '=0': 'Organizations',
        '=1': 'Organization',
        other: 'Organizations',
    };
    readonly unitsMappingTerm: I18nMap = {
        '=0': 'Units',
        '=1': 'Unit',
        'other': 'Units',
    };

    readonly context = NotificationContext.PMC;
    readonly filterByDatePlaceholders = PropertyManagementCompanyDateFilters;
    readonly donutChartData = [
        {
            label: 'Open',
            count: 0,
            color: '#EB9658',
            bgColor: '#EB965890',
        },
        {
            label: 'Closed',
            count: 0,
            color: '#53C1A9',
            bgColor: '#53C1A990',
        },
    ];
    readonly saving$ = new BehaviorSubject<boolean>(false);

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _notificationService: NotificationsService,
            private _fb: FormBuilder,
            private _scrollService: ScrollService,
            private _host: HostListenerService,
            private _sidenav: SidenavContentService,
    ) {
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    get tabletMode$() {
        return this._host.isTabletMode();
    }

    get desktopMode$() {
        return this._host.isDesktopMode();
    }

    get largerMode$() {
        return this._host.isLargerDesktopMode();
    }

    ngOnInit(): void {
        this._scrollService.scrollToTop();
        this.pmc$ = this._propertyManagementCompanyData.pmc$;

        this.setRequestsFilterValueChange();
        this.setViolationsFilterValueChange();

        this.notifications$ = this._notificationService.notifications$.asObservable();
    }

    openNotifications() {
        this._sidenav.open(NotificationsSidenavComponent, {
            initialLoadingState: true,
        });
    }

    private setRequestsFilterValueChange(): void {
        const control = this.filters.get('requestsFilterByDate');
        control.valueChanges.pipe(
                map(() => control.disable({emitEvent: false})),
                switchMap(() => {
                    return this._propertyManagementCompanyService.getPropertyManagementRequestFormCounts(control.value)
                            .pipe(untilDestroyed(this));
                }),
                map((counts) => {
                    this.charts = {
                        ...this.charts,
                        requests: this.buildCharts([
                            counts.formSubmissionsOpenedBetweenCount,
                            counts.formSubmissionsClosedBetweenCount,
                        ]),
                    };
                    control.enable({emitEvent: false});
                }),
        ).subscribe();

        control.setValue(PropertyManagementCompanyDateFilters.CURRENT_MONTH, {emitEvent: true});
    }

    private setViolationsFilterValueChange(): void {
        const control = this.filters.get('violationsFilterByDate');
        control.valueChanges.pipe(
                map(() => control.disable({emitEvent: false})),
                switchMap(() => {
                    return this._propertyManagementCompanyService.getPropertyManagementViolationCounts(control.value)
                            .pipe(untilDestroyed(this));
                }),
                map((counts) => {
                    this.charts = {
                        ...this.charts,
                        violations: this.buildCharts([
                            counts.violationsOpenedBetweenCount,
                            counts.violationsClosedBetweenCount,
                        ]),
                    };
                    control.enable({emitEvent: false});
                }),
        ).subscribe();

        control.setValue(PropertyManagementCompanyDateFilters.CURRENT_MONTH, {emitEvent: true});
    }

    private buildCharts(counts: number[]): PropertyManagementCompanyChartProperties {
        const chart: PropertyManagementCompanyChartProperties = {
            count: 0,
            dataset: {
                data: [],
                backgroundColor: [],
                hoverBackgroundColor: [],
                borderColor: [],
                hoverBorderColor: [],
            },
            labels: [],
            options: GlobalDonutGraphOptions,
            total: () => 0,
        };

        for (const i in this.donutChartData) {
            if (this.donutChartData[i]) {
                chart.dataset.data.push(counts[i] || 0);
                chart.dataset.backgroundColor.push(this.donutChartData[i].color);
                chart.dataset.hoverBackgroundColor.push(this.donutChartData[i].bgColor);
                chart.dataset.borderColor.push('#FFFFFF');
                chart.dataset.hoverBorderColor.push('#FFFFFF');
                chart.labels.push(this.donutChartData[i].label);
                chart.total = () => chart.dataset.data.reduce((a, b) => a + b, 0) || 0;
            }
        }

        return chart;
    }
}
